import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Ops from './Ops';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
  Users, 
  Send, 
  MessageSquare, 
  HeartPulse,
  TrendingUp,
  Flame,
  Thermometer,
  Snowflake,
  CheckCircle,
  RefreshCw,
  Loader2,
  Globe,
  Eye,
  Activity,
  MailCheck,
  UserPlus
} from 'lucide-react';
import { CalendarClock, AlertTriangle, Sparkles } from 'lucide-react';
import { SupplierOnboarding } from '../components/SupplierOnboarding';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [site, setSite] = useState(null);
  const [signups, setSignups] = useState([]);
  const [renewals, setRenewals] = useState(null);
  const [nudge, setNudge] = useState(null);
  const [nudging, setNudging] = useState(false);
  const [rankNudge, setRankNudge] = useState(null);
  const [rankNudging, setRankNudging] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchSite();
    fetchSignups();
    fetchRenewals();
    fetchNudges();
    fetchRankNudges();
    const id = setInterval(() => { fetchStats(); fetchSite(); fetchSignups(); }, 20000);
    return () => clearInterval(id);
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSite = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/site-analytics`);
      setSite(data);
    } catch (error) {
      // non-fatal - dashboard still renders without live traffic
    }
  };

  const fetchSignups = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/recent-signups`);
      setSignups(data.signups || []);
    } catch (error) {
      // non-fatal
    }
  };

  const fetchRenewals = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/directory/renewals`);
      setRenewals(data);
    } catch (error) {
      // non-fatal
    }
  };

  const fetchNudges = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/directory/richness-nudges`);
      setNudge(data);
    } catch (error) {
      // non-fatal
    }
  };

  const sendNudges = async () => {
    if (!nudge || nudge.eligible_now === 0) return;
    if (!window.confirm(`Send a richness nudge to ${nudge.eligible_now} supplier(s) whose listing is under 100%?`)) return;
    setNudging(true);
    try {
      const { data } = await axios.post(`${API}/admin/directory/richness-nudges/send`);
      toast.success(data.message || `Nudges processed (${data.sent} sent)`);
      fetchNudges();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send nudges');
    } finally {
      setNudging(false);
    }
  };

  const fetchRankNudges = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/directory/rank-nudges`);
      setRankNudge(data);
    } catch (error) {
      // non-fatal
    }
  };

  const sendRankNudges = async () => {
    if (!rankNudge || rankNudge.eligible_now === 0) return;
    if (!window.confirm(`Email ${rankNudge.eligible_now} supplier(s) who can climb the rankings by reordering a category?`)) return;
    setRankNudging(true);
    try {
      const { data } = await axios.post(`${API}/admin/directory/rank-nudges/send`);
      toast.success(data.message || `Rank nudges processed (${data.sent} sent)`);
      fetchRankNudges();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send rank nudges');
    } finally {
      setRankNudging(false);
    }
  };

  const runSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/directory/sync-import`);
      setSyncResult(data.summary);
      toast.success('Directory data synced');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-10 w-64 bg-gray-200 rounded" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-gray-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Leads',
      value: stats?.leads?.total || 0,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      subtitle: `${stats?.leads?.hot || 0} hot, ${stats?.leads?.warm || 0} warm`
    },
    {
      title: 'Emails Sent',
      value: stats?.emails?.sent || 0,
      icon: Send,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      subtitle: `${stats?.emails?.sent_today || 0} sent today · ${stats?.emails?.open_rate || 0}% open rate`
    }
  ];

  const leadBreakdown = [
    { label: 'Hot', value: stats?.leads?.hot || 0, icon: Flame, color: 'text-red-600', bgColor: 'bg-red-50' },
    { label: 'Warm', value: stats?.leads?.warm || 0, icon: Thermometer, color: 'text-orange-600', bgColor: 'bg-orange-50' },
    { label: 'Cold', value: stats?.leads?.cold || 0, icon: Snowflake, color: 'text-blue-600', bgColor: 'bg-blue-50' }
  ];

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard">
      {/* Supplier onboarding (only renders for supplier sub-account users) */}
      <SupplierOnboarding />

      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Dashboard</h1>
        <p className="text-gray-600 text-lg">Overview of your outreach intelligence</p>
      </div>

      {/* Sync directory data */}
      <Card className="bg-white border-[#00A79D]/30 shadow-sm" data-testid="dashboard-sync-card">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="p-3 rounded-xl bg-[#00A79D]/10 shrink-0">
              <RefreshCw className="w-6 h-6 text-[#00857D]" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">Sync directory data</h3>
              <p className="text-sm text-gray-500 mt-0.5">Import the full supplier catalogue (products, descriptions, images) and consolidate duplicate suppliers into this environment's database. Safe to run repeatedly - it only adds what's missing and never deletes real data.</p>
              {syncResult && (
                <div className="mt-3 bg-[#00A79D]/5 border border-[#00A79D]/20 rounded-lg p-3 text-sm text-gray-700 space-y-0.5" data-testid="dashboard-sync-result">
                  <p>✅ Synced <b>{syncResult.suppliers_in_snapshot}</b> suppliers - created <b>{syncResult.created}</b>, updated <b>{syncResult.updated}</b>, products added <b>{syncResult.products_added}</b>, duplicates removed <b>{syncResult.fragments_deleted}</b>.</p>
                  <p>The Access Group now has <b>{syncResult.access_group_products}</b> products.</p>
                </div>
              )}
            </div>
            <button
              onClick={runSync}
              disabled={syncing}
              data-testid="dashboard-run-sync"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[#00A79D] text-white text-sm font-semibold rounded-lg hover:bg-[#00857D] disabled:opacity-60 shrink-0"
            >
              {syncing ? <><Loader2 className="w-4 h-4 animate-spin" />Syncing…</> : <><RefreshCw className="w-4 h-4" />Run sync now</>}
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, i) => (
          <Card key={i} className="bg-white border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-testid={`stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-wider text-gray-500 mb-1">
                    {stat.title}
                  </p>
                  <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                  <p className="text-base text-gray-500 mt-1">{stat.subtitle}</p>
                </div>
                <div className={`p-3 rounded-xl ${stat.bgColor}`}>
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Renewals - who's expiring / lapsed */}
      {renewals && (renewals.counts.expiring_30 + renewals.counts.expiring_60 + renewals.counts.lapsed > 0) && (
        <div data-testid="dashboard-renewals">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2 mb-3">
            <CalendarClock className="w-5 h-5 text-[#00857D]" /> Renewals
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {[
              { key: 'lapsed', title: 'Lapsed', rows: renewals.lapsed, tone: 'red', icon: AlertTriangle, sub: 'downgraded to free' },
              { key: 'expiring_30', title: 'Expiring ≤ 30 days', rows: renewals.expiring_30, tone: 'amber', sub: 'chase renewal' },
              { key: 'expiring_60', title: 'Expiring 31-60 days', rows: renewals.expiring_60, tone: 'slate', sub: 'coming up' },
            ].map((col) => (
              <Card key={col.key} className="bg-white border-gray-200 shadow-sm" data-testid={`renewals-${col.key}`}>
                <CardContent className="pt-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {col.icon && <col.icon className={`w-4 h-4 ${col.tone === 'red' ? 'text-red-500' : col.tone === 'amber' ? 'text-amber-500' : 'text-slate-400'}`} />}
                      <span className="font-semibold text-gray-900">{col.title}</span>
                    </div>
                    <span className={`text-sm font-bold px-2 py-0.5 rounded-full ${col.tone === 'red' ? 'bg-red-50 text-red-700' : col.tone === 'amber' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{col.rows.length}</span>
                  </div>
                  {col.rows.length === 0 ? (
                    <p className="text-sm text-gray-400">None</p>
                  ) : (
                    <ul className="space-y-1.5 max-h-56 overflow-y-auto">
                      {col.rows.slice(0, 12).map((r) => (
                        <li key={r.id} className="flex items-center justify-between gap-2 text-sm">
                          <span className="text-gray-700 truncate">{r.name}</span>
                          <span className="text-xs text-gray-400 shrink-0">{r.lapsed ? new Date(r.expires_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : `${r.days_left}d`}</span>
                        </li>
                      ))}
                      {col.rows.length > 12 && <li className="text-xs text-gray-400">+{col.rows.length - 12} more</li>}
                    </ul>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Profile richness nudges - email paying suppliers under 100% */}
      {nudge && nudge.total_incomplete > 0 && (
        <div data-testid="dashboard-richness-nudges">
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-5">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-3 flex-1">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#00A79D]/10 text-[#00857D]"><Sparkles className="w-5 h-5" /></span>
                  <div>
                    <p className="font-semibold text-gray-900">Profile richness nudges</p>
                    <p className="text-sm text-gray-500" data-testid="nudge-summary">
                      {nudge.total_incomplete} paying supplier{nudge.total_incomplete === 1 ? '' : 's'} under 100% · <span className="font-semibold text-[#00857D]">{nudge.eligible_now} ready to nudge</span>
                      {nudge.skipped_recent > 0 ? ` · ${nudge.skipped_recent} nudged in last 7 days` : ''}
                    </p>
                  </div>
                </div>
                <button onClick={sendNudges} disabled={nudging || nudge.eligible_now === 0} data-testid="nudge-send-btn"
                  className="shrink-0 inline-flex items-center gap-2 bg-[#00A79D] hover:bg-[#00857D] disabled:opacity-50 text-white font-semibold text-sm px-4 py-2.5 rounded-md transition-colors">
                  {nudging ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {nudge.eligible_now === 0 ? 'All nudged recently' : `Send ${nudge.eligible_now} nudge${nudge.eligible_now === 1 ? '' : 's'}`}
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Rank-up nudges - help paying suppliers climb by reordering a category */}
      {rankNudge && rankNudge.total_can_climb > 0 && (
        <div data-testid="dashboard-rank-nudges">
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-5">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-3 flex-1">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-amber-50 text-amber-600"><TrendingUp className="w-5 h-5" /></span>
                  <div>
                    <p className="font-semibold text-gray-900">Rank-up nudges</p>
                    <p className="text-sm text-gray-500" data-testid="rank-nudge-summary">
                      {rankNudge.total_can_climb} paying supplier{rankNudge.total_can_climb === 1 ? '' : 's'} could climb by reordering a category · <span className="font-semibold text-amber-700">{rankNudge.eligible_now} ready to nudge</span>
                      {rankNudge.skipped_recent > 0 ? ` · ${rankNudge.skipped_recent} nudged in last 7 days` : ''}
                    </p>
                  </div>
                </div>
                <button onClick={sendRankNudges} disabled={rankNudging || rankNudge.eligible_now === 0} data-testid="rank-nudge-send-btn"
                  className="shrink-0 inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-semibold text-sm px-4 py-2.5 rounded-md transition-colors">
                  {rankNudging ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {rankNudge.eligible_now === 0 ? 'All nudged recently' : `Send ${rankNudge.eligible_now} nudge${rankNudge.eligible_now === 1 ? '' : 's'}`}
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Website traffic (live) */}
      <div data-testid="dashboard-website-analytics">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#00857D]" /> Website traffic
          </h2>
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1" data-testid="dashboard-live-badge">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            Live · updates every 20s
          </span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {[
            { title: 'Live visitors', value: site?.live_visitors ?? 0, sub: 'active in last 5 min', icon: Activity, color: 'text-emerald-600', bg: 'bg-emerald-50', tid: 'live-visitors' },
            { title: 'Visitors today', value: site?.today?.visitors ?? 0, sub: 'unique sessions', icon: Users, color: 'text-blue-600', bg: 'bg-blue-50', tid: 'visitors-today' },
            { title: 'Page views today', value: site?.today?.pageviews ?? 0, sub: 'across the site', icon: Eye, color: 'text-violet-600', bg: 'bg-violet-50', tid: 'pageviews-today' },
            { title: 'Emails sent today', value: site?.emails_today ?? stats?.emails?.sent_today ?? 0, sub: 'all send paths', icon: MailCheck, color: 'text-[#00857D]', bg: 'bg-[#00A79D]/10', tid: 'emails-today' },
          ].map((c, i) => (
            <Card key={i} className="bg-white border-gray-200 shadow-sm" data-testid={`site-stat-${c.tid}`}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">{c.title}</p>
                    <p className="text-3xl font-bold text-gray-900">{Number(c.value).toLocaleString()}</p>
                    <p className="text-xs text-gray-400 mt-1">{c.sub}</p>
                  </div>
                  <div className={`p-3 rounded-xl ${c.bg}`}><c.icon className={`w-6 h-6 ${c.color}`} /></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* 7-day trend */}
          <Card className="bg-white border-gray-200 shadow-sm" data-testid="site-trend-card">
            <CardHeader><CardTitle className="text-lg font-semibold text-gray-900">Visitors - last 7 days</CardTitle></CardHeader>
            <CardContent>
              {(() => {
                const trend = site?.trend || [];
                const max = Math.max(1, ...trend.map((t) => t.visitors));
                return (
                  <div className="flex items-end justify-between gap-2 h-40">
                    {trend.map((t, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center gap-2" data-testid={`site-trend-bar-${t.day}`}>
                        <span className="text-xs font-semibold text-gray-600">{t.visitors}</span>
                        <div className="w-full bg-gray-100 rounded-t-md flex items-end" style={{ height: '110px' }}>
                          <div className="w-full bg-[#00A79D] rounded-t-md transition-all" style={{ height: `${(t.visitors / max) * 100}%` }} />
                        </div>
                        <span className="text-[10px] text-gray-400">{new Date(t.day).toLocaleDateString('en-GB', { weekday: 'short' })}</span>
                      </div>
                    ))}
                    {trend.length === 0 && <p className="text-sm text-gray-400 m-auto">No traffic data yet.</p>}
                  </div>
                );
              })()}
            </CardContent>
          </Card>

          {/* Top pages */}
          <Card className="bg-white border-gray-200 shadow-sm" data-testid="site-top-pages-card">
            <CardHeader><CardTitle className="text-lg font-semibold text-gray-900">Top pages (7 days)</CardTitle></CardHeader>
            <CardContent>
              {(site?.top_pages || []).length === 0 ? (
                <p className="text-sm text-gray-400">No page views recorded yet.</p>
              ) : (
                <div className="space-y-2.5">
                  {(site?.top_pages || []).map((p, i) => (
                    <div key={i} className="flex items-center justify-between text-sm" data-testid={`site-top-page-${i}`}>
                      <span className="truncate text-gray-700 mr-3 font-mono text-xs">{p.path}</span>
                      <span className="whitespace-nowrap text-gray-500"><b className="text-gray-900">{p.views}</b> views · {p.visitors} visitors</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Traffic sources */}
        <Card className="bg-white border-gray-200 shadow-sm mt-6" data-testid="site-sources-card">
          <CardHeader><CardTitle className="text-lg font-semibold text-gray-900">Where visitors come from (7 days)</CardTitle></CardHeader>
          <CardContent>
            {(() => {
              const sources = site?.sources || [];
              const total = sources.reduce((s, x) => s + x.visitors, 0);
              const COLORS = { Google: 'bg-blue-500', Search: 'bg-sky-500', Direct: 'bg-slate-400', Social: 'bg-violet-500', Referral: 'bg-amber-500', Campaign: 'bg-[#00A79D]' };
              if (total === 0) return <p className="text-sm text-gray-400">No traffic data yet. Sources appear as visitors arrive.</p>;
              return (
                <div className="space-y-3">
                  <div className="flex w-full h-3 rounded-full overflow-hidden bg-gray-100" data-testid="site-sources-bar">
                    {sources.map((s, i) => (
                      <div key={i} className={`${COLORS[s.source] || 'bg-gray-400'} h-full`} style={{ width: `${(s.visitors / total) * 100}%` }} title={`${s.source}: ${s.visitors}`} />
                    ))}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {sources.map((s, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm" data-testid={`site-source-${s.source}`}>
                        <span className={`w-2.5 h-2.5 rounded-full ${COLORS[s.source] || 'bg-gray-400'}`} />
                        <span className="text-gray-700">{s.source}</span>
                        <span className="text-gray-400 ml-auto">{s.visitors} ({Math.round((s.visitors / total) * 100)}%)</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* Campaign performance: clicks + conversions */}
        {((site?.campaigns || []).length > 0 || (site?.campaign_conversions || []).length > 0) && (
          <Card className="bg-white border-gray-200 shadow-sm mt-6" data-testid="site-campaigns-card">
            <CardHeader><CardTitle className="text-lg font-semibold text-gray-900">Campaign performance - clicks &amp; conversions (7 days)</CardTitle></CardHeader>
            <CardContent>
              {(() => {
                const merged = {};
                (site?.campaigns || []).forEach((c) => { merged[c.campaign_id] = { name: c.campaign, visitors: c.visitors, webinar: 0, claim: 0, claim_started: 0 }; });
                (site?.campaign_conversions || []).forEach((c) => {
                  merged[c.campaign_id] = { ...(merged[c.campaign_id] || { name: c.campaign, visitors: 0 }), webinar: c.webinar || 0, claim: c.claim || 0, claim_started: c.claim_started || 0 };
                });
                const rows = Object.values(merged).sort((a, b) => (b.webinar + b.claim) - (a.webinar + a.claim) || b.visitors - a.visitors);
                return (
                  <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
                        <th className="py-2 font-semibold">Campaign</th>
                        <th className="py-2 font-semibold text-right">Visitors</th>
                        <th className="py-2 font-semibold text-right">Webinar sign-ups</th>
                        <th className="py-2 font-semibold text-right">Claims</th>
                        <th className="py-2 font-semibold text-right">Conv. rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => {
                        const conversions = (r.webinar || 0) + (r.claim || 0);
                        const rate = r.visitors > 0 ? Math.round((conversions / r.visitors) * 1000) / 10 : 0;
                        return (
                          <tr key={i} className="border-b border-gray-50 last:border-0" data-testid={`campaign-perf-${i}`}>
                            <td className="py-2.5 text-gray-800 truncate max-w-[260px]">{r.name}</td>
                            <td className="py-2.5 text-right font-mono text-gray-600">{r.visitors}</td>
                            <td className="py-2.5 text-right font-mono text-violet-600 font-semibold">{r.webinar}</td>
                            <td className="py-2.5 text-right font-mono text-[#00857D] font-semibold">
                              {r.claim}
                              {r.claim_started > r.claim ? <span className="text-gray-400 font-normal text-xs"> / {r.claim_started} started</span> : null}
                            </td>
                            <td className="py-2.5 text-right font-mono">
                              <span className={rate >= 5 ? 'text-emerald-600 font-semibold' : rate > 0 ? 'text-gray-700' : 'text-gray-400'}>{rate}%</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                );
              })()}
              <p className="text-[11px] text-gray-400 mt-3">Conversion rate = (webinar sign-ups + completed claims) ÷ visitors. Claims count fully-paid claims; "started" shows claims begun but not yet paid.</p>
            </CardContent>
          </Card>
        )}

        {/* Campaign funnel: visitors -> started -> completed */}
        {(site?.campaign_conversions || []).some((c) => (c.claim_started || 0) + (c.claim || 0) > 0) && (
          <Card className="bg-white border-gray-200 shadow-sm mt-6" data-testid="site-funnel-card">
            <CardHeader><CardTitle className="text-lg font-semibold text-gray-900">Claim funnel - where people drop off (7 days)</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              {(() => {
                const vis = {};
                (site?.campaigns || []).forEach((c) => { vis[c.campaign_id] = { name: c.campaign, visitors: c.visitors }; });
                const rows = (site?.campaign_conversions || [])
                  .filter((c) => (c.claim_started || 0) + (c.claim || 0) > 0)
                  .map((c) => ({
                    name: c.campaign,
                    visitors: vis[c.campaign_id]?.visitors || 0,
                    started: c.claim_started || 0,
                    completed: c.claim || 0,
                  }))
                  .sort((a, b) => b.completed - a.completed || b.started - a.started);
                const stage = (label, value, base, color) => {
                  const pct = base > 0 ? Math.round((value / base) * 100) : 0;
                  return (
                    <div className="flex items-center gap-3">
                      <span className="w-20 shrink-0 text-xs text-gray-500">{label}</span>
                      <div className="flex-1 h-6 rounded-md bg-gray-100 overflow-hidden">
                        <div className={`h-full ${color} rounded-md flex items-center justify-end px-2 transition-all`} style={{ width: `${Math.max(pct, value > 0 ? 6 : 0)}%` }}>
                          {value > 0 && <span className="text-[11px] font-semibold text-white">{value}</span>}
                        </div>
                      </div>
                      <span className="w-12 shrink-0 text-right text-xs text-gray-400">{pct}%</span>
                    </div>
                  );
                };
                return rows.map((r, i) => (
                  <div key={i} data-testid={`funnel-${i}`}>
                    <p className="text-sm font-semibold text-gray-800 mb-2 truncate">{r.name}</p>
                    <div className="space-y-1.5">
                      {stage('Visitors', r.visitors, r.visitors, 'bg-slate-400')}
                      {stage('Started', r.started, r.visitors, 'bg-amber-500')}
                      {stage('Completed', r.completed, r.visitors, 'bg-[#00A79D]')}
                    </div>
                    {r.started > r.completed && (
                      <p className="text-[11px] text-amber-600 mt-1.5">{r.started - r.completed} started a claim but didn't finish - worth a nudge.</p>
                    )}
                  </div>
                ));
              })()}
            </CardContent>
          </Card>
        )}

        {/* Recent sign-ups */}
        <Card className="bg-white border-gray-200 shadow-sm mt-6" data-testid="dashboard-recent-signups">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#00857D]" /> Recent sign-ups
            </CardTitle>
          </CardHeader>
          <CardContent>
            {signups.length === 0 ? (
              <p className="text-sm text-gray-400">No sign-ups yet. Webinar and platform registrations will appear here live.</p>
            ) : (
              <div className="divide-y divide-gray-100">
                {signups.map((s, i) => (
                  <div key={i} className="flex items-center gap-3 py-2.5" data-testid={`signup-row-${i}`}>
                    <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full ${s.type === 'webinar' ? 'bg-violet-50 text-violet-700' : 'bg-[#00A79D]/10 text-[#00857D]'}`}>
                      {s.type === 'webinar' ? 'Webinar' : 'Platform'}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{s.name}{s.org ? ` · ${s.org}` : ''}</p>
                      <p className="text-xs text-gray-500 truncate">{s.email} - {s.detail}</p>
                    </div>
                    <span className="shrink-0 text-xs text-gray-400 whitespace-nowrap">
                      {s.at ? new Date(s.at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lead Temperature */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              Lead Temperature
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {leadBreakdown.map((item, i) => (
              <div key={i} className="flex items-center gap-4">
                <div className={`p-2 rounded-lg ${item.bgColor}`}>
                  <item.icon className={`w-5 h-5 ${item.color}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-base font-medium text-gray-700">{item.label}</span>
                    <span className="text-base font-semibold text-gray-600">{item.value}</span>
                  </div>
                  <Progress 
                    value={stats?.leads?.total ? (item.value / stats.leads.total) * 100 : 0} 
                    className="h-2"
                  />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Audience Health */}
        <Card className="bg-white border-gray-200 shadow-sm" data-testid="audience-health-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <HeartPulse className="w-5 h-5 text-emerald-600" />
              Audience Health
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const a = stats?.audience || {};
              const emailable = a.emailable || 0;
              const pct = (n) => emailable ? Math.round((n / emailable) * 100) : 0;
              const rows = [
                { label: 'Subscribed', value: a.subscribed || 0, color: 'bg-emerald-500', testid: 'audience-subscribed' },
                { label: 'Never opened', value: a.never_opened || 0, color: 'bg-amber-500', testid: 'audience-never-opened' },
                { label: 'Opened before', value: a.opened_ever || 0, color: 'bg-blue-500', testid: 'audience-opened' },
                { label: 'Unsubscribed', value: a.unsubscribed || 0, color: 'bg-rose-500', testid: 'audience-unsubscribed' },
                { label: 'Bounced / complained', value: a.bounced || 0, color: 'bg-gray-400', testid: 'audience-bounced' },
              ];
              return (
                <div className="space-y-3.5">
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm text-gray-500">Emailable contacts</span>
                    <span className="text-2xl font-bold text-gray-900" data-testid="audience-emailable">{emailable.toLocaleString()}</span>
                  </div>
                  {rows.map((r, i) => (
                    <div key={i} data-testid={r.testid}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-700">{r.label}</span>
                        <span className="text-sm font-medium text-gray-600">{r.value.toLocaleString()} <span className="text-gray-400">({pct(r.value)}%)</span></span>
                      </div>
                      <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                        <div className={`h-full ${r.color} rounded-full transition-all`} style={{ width: `${pct(r.value)}%` }} />
                      </div>
                    </div>
                  ))}
                  {(stats?.emails?.sent || 0) === 0 && (
                    <p className="text-xs text-gray-400 pt-1">Open data appears once you send your first campaign.</p>
                  )}
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-900">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <button className="w-full px-4 py-3 text-left bg-blue-50 hover:bg-blue-100 rounded-xl text-blue-700 font-medium transition-colors text-base">
              → Compose a bulk email
            </button>
            <button className="w-full px-4 py-3 text-left bg-emerald-50 hover:bg-emerald-100 rounded-xl text-emerald-700 font-medium transition-colors text-base">
              → View your contacts
            </button>
          </CardContent>
        </Card>
      </div>
      <Card className="bg-white border-gray-200 shadow-sm" data-testid="dashboard-ops-card">
        <CardHeader><CardTitle className="text-lg">Email queue, product unlocks &amp; assurance requests</CardTitle></CardHeader>
        <CardContent className="max-h-[520px] overflow-y-auto"><Ops embedded /></CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;